import { useRef, useState } from 'react';
import { matchIsValidTel } from 'mui-tel-input';
import { ProfileSettingsData, SaveProfileSettingsRequest } from 'src/model/api/v2/data-contracts';
import { useSaveProfileSettingsMutation } from 'src/api/v2/profile-settings-v2-ApiSlice';
import { appApiSlice } from 'src/api/app-ApiSlice';
import {
    TAG_FLOOR_ROOMS_AND_RELATED,
    TAG_GET_DEVICES,
    TAG_GET_PRODUCTS_OF_ROOMS_BY_FLOORS,
    TAG_PROJECT,
    TAG_PROJECTS,
} from 'src/api/api-tags';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { clearVatNumberIfNotApplicable, validateCompany, validateVatNumber } from 'src/utils/vat';
import { EMAIL_PATTERN } from 'src/utils/email';
import { ts } from 'src/utils/translate';

export type ProfileForm = SaveProfileSettingsRequest;
export type ProfileFormField = keyof ProfileForm;
export type ProfileFormErrors = Partial<Record<ProfileFormField, string>>;

const REQUIRED_FIELDS: ProfileFormField[] = ['first_name', 'last_name', 'email'];
const MIN_LENGTH_FIELDS: ProfileFormField[] = [
    'first_name', 'last_name', 'website', 'company', 'tax_number', 'country', 'city', 'street_address', 'zipcode',
];
const MIN_LENGTH = 2;

const toForm = (profile: ProfileSettingsData): ProfileForm => ({
    first_name: profile.first_name,
    last_name: profile.last_name,
    website: profile.website,
    email: profile.email,
    company: profile.company,
    tax_number: profile.tax_number,
    country: profile.country,
    city: profile.city,
    street_address: profile.street_address,
    zipcode: profile.zipcode,
    telephone: profile.telephone,
    site_language: profile.site_language,
    preferred_currency_id: profile.preferred_currency_id,
    planner_icon_set_id: profile.planner_icon_set_id,
});

const validate = (form: ProfileForm, savedForm: ProfileForm): ProfileFormErrors => {
    const errors: ProfileFormErrors = {};

    MIN_LENGTH_FIELDS.forEach(field => {
        const value = form[field].toString().trim();
        if (value !== '' && value.length < MIN_LENGTH) {
            errors[field] = ts('profile_settings_min_length', 'This field should contain at least 2 characters.');
        }
    });

    if (form.email.trim() !== '' && !EMAIL_PATTERN.test(form.email.trim())) {
        errors.email = ts('profile_settings_invalid_email', 'Please enter a valid email address.');
    }

    // only a changed number is checked, so an old number saved in another format doesn't block saving the rest
    if (form.telephone !== savedForm.telephone && form.telephone.trim() !== '' && !matchIsValidTel(form.telephone)) {
        errors.telephone = ts('profile_settings_invalid_telephone', 'Please enter a valid phone number.');
    }

    REQUIRED_FIELDS.forEach(field => {
        if (form[field].toString().trim() === '') {
            errors[field] = ts('jqval_msg_required', 'This field is required.');
        }
    });

    const companyError = validateCompany(form);
    if (companyError) errors.company = companyError;

    const taxNumberError = validateVatNumber(form);
    if (taxNumberError) errors.tax_number = taxNumberError;

    return errors;
};

const isSameForm = (a: ProfileForm, b: ProfileForm) =>
    (Object.keys(a) as ProfileFormField[]).every(field => a[field] === b[field]);

export function useProfileSettingsForm(profile: ProfileSettingsData) {
    const dispatch = useAppDispatch();
    const [saveProfileSettings] = useSaveProfileSettingsMutation();

    const [form, setForm] = useState<ProfileForm>(() => toForm(profile));
    const [formValidationErrors, setFormValidationErrors] = useState<ProfileFormErrors>({});
    // initialised once: a refetch of the profile must not overwrite fields the user is editing
    const lastSavedForm = useRef<ProfileForm>(toForm(profile));

    const save = (nextForm: ProfileForm) => {
        const validationErrors = validate(nextForm, lastSavedForm.current);
        setFormValidationErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0 || isSameForm(nextForm, lastSavedForm.current)) {
            return;
        }

        const isLanguageChanged = nextForm.site_language !== lastSavedForm.current.site_language;
        const isIconSetChanged = nextForm.planner_icon_set_id !== lastSavedForm.current.planner_icon_set_id;

        saveProfileSettings(nextForm)
            .unwrap()
            .then(() => {
                lastSavedForm.current = nextForm;
                if (isIconSetChanged) {
                    // the device symbols of the planner come from the icon set as well
                    dispatch(appApiSlice.util.invalidateTags([
                        TAG_PROJECTS,
                        TAG_PROJECT,
                        TAG_GET_DEVICES,
                        TAG_GET_PRODUCTS_OF_ROOMS_BY_FLOORS,
                        TAG_FLOOR_ROOMS_AND_RELATED,
                    ]));
                }
                if (isLanguageChanged) {
                    window.location.reload();
                    return;
                }
                dispatch(showSnackBar({
                    text: ts('profile_details_saved', 'Profile details saved!'),
                    severity: 'success',
                }));
            })
            .catch((error) => {
                const errorParams = error?.data?.errorParams as ProfileFormErrors | null | undefined;
                if (errorParams) {
                    setFormValidationErrors(errorParams);
                    return;
                }
                if (error?.data?.errorMessage == null) {
                    dispatch(showSnackBar({
                        text: ts('profile_settings_save_error', 'Failed to save profile settings'),
                        severity: 'error',
                    }));
                }
            });
    };

    const changeField = <F extends ProfileFormField>(field: F, value: ProfileForm[F]) => {
        setForm(prevForm => clearVatNumberIfNotApplicable({ ...prevForm, [field]: value }, field));
        setFormValidationErrors(prevErrors => ({ ...prevErrors, [field]: undefined }));
    };

    const commitForm = () => save(form);

    const changeAndSave = <F extends ProfileFormField>(field: F, value: ProfileForm[F]) => {
        const nextForm = clearVatNumberIfNotApplicable({ ...form, [field]: value }, field);
        setForm(nextForm);
        save(nextForm);
    };

    return { form, formValidationErrors, changeField, commitForm, changeAndSave };
}
